import axios from "axios";
import { API_BASE_URL } from "./api";

const TOKEN_KEY = "sensify_token";
const REFRESH_TOKEN_KEY = "sensify_refresh_token";

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json"
    },
    timeout: 10000
});

axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null) => {
    failedQueue.forEach(({ resolve, reject }) => (token ? resolve(token) : reject(error)));
    failedQueue = [];
};

axiosInstance.interceptors.response.use(
    (response) => {
        const method = response.config.method?.toUpperCase();
        const isMutation = ["POST", "PUT", "DELETE", "PATCH"].includes(method ?? "");
        if (isMutation && response.data && response.data.success === false) {
            return Promise.reject(new Error(response.data.message || "İşlem başarısız."));
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (!refreshToken) {
                localStorage.removeItem(TOKEN_KEY);
                window.location.href = "/login";
                return Promise.reject(error);
            }

            try {
                const res = await axios.post(`${API_BASE_URL}/Auth/Refresh`, { refreshToken });
                const newAccess: string = res.data.accessToken;
                const newRefresh: string = res.data.refreshToken;
                localStorage.setItem(TOKEN_KEY, newAccess);
                localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);
                axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
                processQueue(null, newAccess);
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
                window.location.href = "/login";
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        const message = error.response?.data?.message || error.message || "Bir hata oluştu";
        return Promise.reject(new Error(message));
    }
);

export default axiosInstance;
